import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, useFormikContext, Form } from 'formik';
import { useRifm } from 'rifm';
import {
  Box,
  Button,
  CircularProgress,
  Checkbox,
  TextField,
} from '@iclinic/design-system';

import * as S from './ClinicForm.styles';
import { clinicValidationSchema } from '../validation';
import useStyles from './dialogStyles.style';
import { close, submitClinic, setClinicBillingFormValues } from '../state';
import { CFMUpdateSteps, ClinicFormValues } from '../types';
import {
  requestStatusDerivedSelector,
  clinicFormSelector,
  getUserInfo,
  getPossibleStepsSelector,
} from '../state/selectors';
import { cepFormatter, cnpjFormatter } from 'shared/utils/formatters';
import { useGetZipCodeAddress } from './hooks';
import ErrorMessage from './ErrorMessage';
import ClinicFormFields from './ClinicFormFields';
import CustomTextField from './CustomTextField';
import BRAZIL_STATES_INITIALS from 'shared/constants/brStates';
import { PERSON_KIND } from '../constants';

const submitButtonContent = (isLoading: boolean) =>
  isLoading ? <CircularProgress size="sm" /> : 'Confirmar';

const errorMessage = (hasError: boolean) => hasError && <ErrorMessage />;

const getBillingFormValues = (
  values: ClinicFormValues,
  useClinicAddress: boolean,
) => ({
  person_kind: PERSON_KIND.PF,
  responsible: '',
  cpf_cnpj: '',
  billing_zip: useClinicAddress ? values.zip_code : '',
  billing_city: useClinicAddress ? values.city : '',
  billing_state: useClinicAddress ? values.state : '',
  billing_address: useClinicAddress ? values.address : '',
  billing_number: useClinicAddress ? values.number : '',
  billing_address2: useClinicAddress ? values.complement : '',
  billing_neighborhood: useClinicAddress ? values.neighborhood : '',
  billing_country: 'BR',
});

const ClinicForm = () => {
  const classes = useStyles();
  const {
    errors,
    touched,
    handleChange,
    handleBlur,
    values,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<ClinicFormValues>();
  const dispatch = useDispatch();

  const { isPending, hasError } = useSelector(requestStatusDerivedSelector);
  const { isAdmin } = useSelector(getUserInfo);
  const possibleSteps = useSelector(getPossibleStepsSelector);
  const hasClinicBilling = possibleSteps.includes(CFMUpdateSteps.clinicBilling);

  const { getAddress, zipCodeIsLoading } = useGetZipCodeAddress();

  const { value: zipCode, onChange: handleChangeZipCode } = useRifm({
    value: values.zip_code,
    onChange: (value: string) => {
      setFieldValue('zip_code', value);
      if (value.length === 9 && value !== values.zip_code) {
        getAddress(value);
      }
    },
    format: cepFormatter,
  });

  const { value: clinicCnpj, onChange: handleChangeCnpj } = useRifm({
    value: values.clinic_cnpj,
    onChange: (value: string) => {
      setFieldValue('clinic_cnpj', value);
    },
    format: cnpjFormatter,
  });

  useEffect(() => {
    if (values.zip_code.length === 9) {
      setFieldTouched('zip_code', true);
    }
  }, [values.zip_code, setFieldTouched]);

  const formDisabled = isPending ?? zipCodeIsLoading;

  return (
    <Form>
      <ClinicFormFields
        values={values}
        errors={errors}
        touched={touched}
        handleChange={handleChange}
        handleBlur={handleBlur}
        formDisabled={formDisabled}
        zipCode={zipCode}
        handleChangeZipCode={handleChangeZipCode}
        zipCodeIsLoading={zipCodeIsLoading}
        clinicCnpj={clinicCnpj}
        handleChangeCnpj={handleChangeCnpj}
      />

      <Box display="flex" justifyContent="space-between" mb={2}>
        <CustomTextField
          label="Cidade"
          id="city"
          name="city"
          type="text"
          placeholder="Ex: Ribeirão Preto"
          autoComplete="city"
          margin="normal"
          error={!!(touched.city && errors.city)}
          helperText={touched.city && errors.city}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.city}
          disabled={formDisabled}
          dataGa="cfmupdate-city"
          fullWidth
          className={classes.fieldMargin}
        />

        <TextField
          id="state"
          label="Estado"
          name="state"
          value={values.state}
          onChange={handleChange}
          error={!!(touched.state && errors.state)}
          helperText={touched.state && errors.state}
          disabled={formDisabled}
          select
          SelectProps={{
            native: true,
          }}
          margin="normal"
        >
          <option value="">Selecione</option>
          {BRAZIL_STATES_INITIALS.map((state) => (
            <option value={state} key={state}>
              {state}
            </option>
          ))}
        </TextField>
      </Box>

      {isAdmin && hasClinicBilling && (
        <Checkbox
          name="clinic_as_billing"
          checked={values.clinic_as_billing}
          onChange={(e) => setFieldValue('clinic_as_billing', e.target.checked)}
          color="primary"
          label="Usar este endereço como endereço de cobrança."
        />
      )}

      {errorMessage(hasError)}
      <S.Box display="flex" justifyContent="flex-end">
        <S.Button
          color="transparent"
          className={classes.exitButton}
          onClick={() => dispatch(close())}
        >
          Cancelar
        </S.Button>
        <Button
          type="submit"
          disabled={formDisabled}
          className={classes.mainButton}
        >
          {submitButtonContent(formDisabled)}
        </Button>
      </S.Box>
    </Form>
  );
};

const ClinicFormContainer = () => {
  const dispatch = useDispatch();
  const clinic = useSelector(clinicFormSelector);

  return (
    <Formik
      enableReinitialize
      initialValues={clinic}
      onSubmit={(values) => {
        const { clinic_as_billing, ...payload } = values;
        dispatch(submitClinic(payload));
        dispatch(
          setClinicBillingFormValues(
            getBillingFormValues(values, clinic_as_billing),
          ),
        );
      }}
      validationSchema={clinicValidationSchema}
    >
      <ClinicForm />
    </Formik>
  );
};
export default ClinicFormContainer;
